import { prettyStage } from '../../lib/adminFormat';
import type { FunnelStage } from '../../types/admin';

interface FunnelBarsProps {
  title: string;
  stages: FunnelStage[];
  /** Pretty labels per stage key; unknown keys are title-cased from SNAKE_CASE. */
  labels?: Record<string, string>;
  loading?: boolean;
  /** Called with the stage key when a row is clicked. */
  onStageClick?: (stage: string) => void;
}

/** Horizontal funnel: each bar is scaled against the first stage's count. */
export default function FunnelBars({ title, stages, labels, loading, onStageClick }: FunnelBarsProps) {
  const base = stages.length > 0 ? Math.max(stages[0].count, 1) : 1;
  return (
    <div className="glass-card p-5">
      <h3 className="text-sm font-semibold text-glass-text">{title}</h3>
      {loading ? (
        <div className="mt-4 space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-7 animate-pulse rounded-md bg-glass-3/60" />
          ))}
        </div>
      ) : stages.length === 0 ? (
        <p className="mt-4 text-sm text-glass-text-3">No data yet.</p>
      ) : (
        <ol className="mt-4 space-y-2">
          {stages.map((s, i) => {
            const pct = Math.max(0, Math.min(100, (s.count / base) * 100));
            const prev = i > 0 ? stages[i - 1].count : null;
            const conv = prev && prev > 0 ? Math.round((s.count / prev) * 100) : null;
            const Row = onStageClick ? 'button' : 'div';
            return (
              <li key={s.stage}>
                <Row
                  type={onStageClick ? 'button' : undefined}
                  onClick={onStageClick ? () => onStageClick(s.stage) : undefined}
                  className={`block w-full text-left ${onStageClick ? 'rounded-md hover:bg-glass-3/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent2/50' : ''}`}
                >
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-glass-text-2">
                      <span className="mr-1 text-glass-text-3">{i + 1}.</span>
                      {prettyStage(s.stage, labels)}
                    </span>
                    <span className="tabular-nums text-glass-text">
                      {s.count.toLocaleString()}
                      {conv != null && <span className="ml-2 text-glass-text-3">{conv}%</span>}
                    </span>
                  </div>
                  <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-glass-3/60">
                    <div
                      className="h-full rounded-full"
                      style={{
                        width: `${pct}%`,
                        background: 'linear-gradient(90deg, rgb(var(--accent-rgb)), rgb(var(--accent2-rgb)))',
                      }}
                    />
                  </div>
                </Row>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}
